import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft, Calendar, Tag } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cf82e0fe-468c-4a43-97a9-405e8da70dca/artifacts/znleb4a2_260207%20Logo%20Galeries%20Yess5zw.png";

const CATEGORY_COLORS = {
    "Style & Closet": "bg-pink-100 text-pink-700",
    "Event Updates": "bg-blue-100 text-blue-700",
    "Community": "bg-purple-100 text-purple-700",
    "Charity": "bg-green-100 text-green-700"
};

const NewsPage = () => {
    const { api } = useAuth();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchArticles();
    }, []);

    const fetchArticles = async () => {
        try {
            const response = await api().get('/news/');
            setArticles(response.data);
        } catch (error) {
            console.error('Failed to fetch articles:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            year: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-white pb-24">
            {/* Fixed Header */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 safe-area-top">
                <div className="flex items-center justify-between px-4 py-4">
                    <button
                        onClick={() => navigate('/menu?section=news')}
                        className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
                        data-testid="back-button"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <img 
                        src={LOGO_URL} 
                        alt="Galeries Yess" 
                        className="h-8 w-auto"
                    />
                    <div className="w-9" />
                </div>
            </div>

            <div className="pt-20 px-4">
                {/* Page Title */}
                <h1 className="text-2xl font-heading font-light tracking-tight mb-2">
                    News
                </h1>
                <p className="text-sm text-gray-400 mb-8">
                    Stories from our salon
                </p>

                {loading ? (
                    <div className="text-center py-16">
                        <div className="w-8 h-8 border-2 border-gray-200 border-t-gray-600 rounded-full animate-spin mx-auto"></div>
                    </div>
                ) : articles.length === 0 ? (
                    <div className="text-center py-16">
                        <p className="text-gray-400 italic">
                            No articles yet. Check back soon!
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {articles.map((article) => (
                            <article 
                                key={article.id}
                                onClick={() => navigate(`/news/${article.id}`)}
                                className="cursor-pointer group"
                                data-testid={`article-card-${article.id}`}
                            >
                                {/* Header Image */}
                                {article.header_image && (
                                    <div className="aspect-[16/9] overflow-hidden mb-4">
                                        <img
                                            src={article.header_image}
                                            alt={article.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                )}
                                
                                {/* Category & Date */}
                                <div className="flex items-center gap-3 mb-2">
                                    <span className={`text-xs px-2 py-1 rounded-full ${CATEGORY_COLORS[article.category] || 'bg-gray-100 text-gray-600'}`}>
                                        {article.category}
                                    </span>
                                    <span className="text-xs text-gray-400 flex items-center gap-1">
                                        <Calendar className="w-3 h-3" />
                                        {formatDate(article.published_at || article.created_at)}
                                    </span>
                                </div>
                                
                                {/* Title */}
                                <h2 className="text-lg font-heading font-medium text-gray-900 group-hover:text-gray-600 transition-colors mb-1">
                                    {article.title}
                                </h2>
                                
                                {/* Subtitle */}
                                {article.subtitle && (
                                    <p className="text-sm text-gray-500 mb-3">
                                        {article.subtitle}
                                    </p>
                                )}
                                
                                {/* Read More Link */}
                                <span className="text-xs text-gray-400 uppercase tracking-wider group-hover:text-gray-600 transition-colors">
                                    Read article →
                                </span>
                                
                                {/* Divider */}
                                <div className="border-b border-gray-100 mt-6"></div>
                            </article>
                        ))}
                    </div>
                )}
            </div>

            <BottomNav />
        </div>
    );
};

export default NewsPage;
